// src/utils/formatAddressHelper.js

/**
 * INTERNATIONAL ADDRESS FORMATTER
 * Handles any address format from anywhere in the world
 */

/**
 * Country-specific address formats
 */
const COUNTRY_FORMATS = {
  // US/Canada style: Street, City, State, ZIP, Country
  'us': ['street', 'city', 'state', 'zip', 'country'],
  'ca': ['street', 'city', 'state', 'zip', 'country'],
  'gb': ['street', 'city', 'county', 'postalCode', 'country'],
  'uk': ['street', 'city', 'county', 'postalCode', 'country'],
  'fr': ['street', 'postalCode', 'city', 'country'],
  'de': ['street', 'postalCode', 'city', 'country'],
  'it': ['street', 'postalCode', 'city', 'country'],
  'es': ['street', 'postalCode', 'city', 'country'],
  'jp': ['postalCode', 'prefecture', 'city', 'street', 'country'],
  'cn': ['country', 'province', 'city', 'district', 'street'],
  'in': ['street', 'city', 'state', 'postalCode', 'country'],
  'au': ['street', 'city', 'state', 'postalCode', 'country'],
  'br': ['street', 'city', 'state', 'postalCode', 'country'],
  'mx': ['street', 'city', 'state', 'postalCode', 'country'],
  'za': ['street', 'city', 'province', 'postalCode', 'country'],
  'ng': ['street', 'city', 'state', 'postalCode', 'country'],
  'ke': ['street', 'city', 'county', 'postalCode', 'country'],
  'eg': ['street', 'city', 'governorate', 'postalCode', 'country'],
  // Haiti and Caribbean
  'ht': ['street', 'city', 'department', 'postalCode', 'country'],
  'do': ['street', 'city', 'province', 'postalCode', 'country'],
  'jm': ['street', 'city', 'parish', 'postalCode', 'country'],
  'tt': ['street', 'city', 'region', 'postalCode', 'country'],
  // Add more as needed
};

/**
 * Get country code from address
 */
const getCountryCode = (address) => {
  if (!address) return null;
  
  // Check for country code field
  if (address.countryCode) return address.countryCode.toLowerCase();
  
  // Try to extract from country name
  const countryName = (address.country || '').toLowerCase();
  const countryMap = {
    'united states': 'us',
    'usa': 'us',
    'canada': 'ca',
    'uk': 'gb',
    'united kingdom': 'gb',
    'france': 'fr',
    'germany': 'de',
    'italy': 'it',
    'spain': 'es',
    'japan': 'jp',
    'china': 'cn',
    'india': 'in',
    'australia': 'au',
    'brazil': 'br',
    'mexico': 'mx',
    'south africa': 'za',
    'nigeria': 'ng',
    'kenya': 'ke',
    'egypt': 'eg',
    'haiti': 'ht',
    'dominican republic': 'do',
    'jamaica': 'jm',
    'trinidad': 'tt',
    'trinidad and tobago': 'tt'
  };
  
  // Check if country name matches any in map
  for (const [name, code] of Object.entries(countryMap)) {
    if (countryName.includes(name)) {
      return code;
    }
  }
  
  return null;
};

/**
 * Get field order for a specific country
 */
const getFieldOrder = (address) => {
  const countryCode = getCountryCode(address);
  if (countryCode && COUNTRY_FORMATS[countryCode]) {
    return COUNTRY_FORMATS[countryCode];
  }
  
  // Default format (most common: Street, City, State/Province, Postal, Country)
  return ['street', 'city', 'state', 'province', 'postalCode', 'zip', 'country'];
};

/**
 * Normalize field names (handle variations)
 */
const normalizeField = (address, fieldName) => {
  if (!address) return '';
  
  // Handle common field name variations
  const fieldMap = {
    'street': ['street', 'address', 'address1', 'line1', 'streetAddress'],
    'city': ['city', 'town', 'municipality', 'locality'],
    'state': ['state', 'province', 'region', 'department', 'county', 'parish', 'governorate'],
    'zip': ['zip', 'zipCode', 'postalCode', 'postcode', 'pin'],
    'postalCode': ['postalCode', 'zip', 'zipCode', 'postcode', 'pin'],
    'country': ['country', 'nation'],
    'district': ['district', 'neighborhood', 'suburb', 'area'],
    'apartment': ['apartment', 'apt', 'unit', 'suite', 'floor']
  };
  
  const possibleFields = fieldMap[fieldName] || [fieldName];
  
  for (const field of possibleFields) {
    if (address[field] && typeof address[field] === 'string' && address[field].trim()) {
      return address[field].trim();
    }
  }
  
  return '';
};

/**
 * MAIN: Format any address from anywhere in the world
 * 
 * @param {Object|string} address - Address object or string
 * @param {Object} options - Formatting options
 * @param {string} options.format - 'full', 'short', 'inline', 'multi-line'
 * @param {boolean} options.includeCountry - Include country in output (default: true)
 * @param {string} options.separator - Separator between fields (default: ', ')
 * @returns {string} Formatted address
 */
export const formatAddress = (address, options = {}) => {
  const {
    format = 'full',
    includeCountry = true,
    separator = ', ',
  } = options;
  
  // If address is null/undefined
  if (!address) return '';
  
  // If address is already a string
  if (typeof address === 'string') {
    return address;
  }
  
  // If there's a pre-formatted field
  if (address.formatted) return address.formatted;
  
  // Check if street contains full address
  const street = address.street || address.address || address.address1 || '';
  const city = address.city || address.town || address.municipality || '';
  
  const hasFullAddress = street && (
    street.includes(',') || 
    (city && street.includes(city)) ||
    (address.state && street.includes(address.state)) ||
    (address.country && street.includes(address.country)) ||
    (address.zip && street.includes(address.zip))
  );
  
  if (hasFullAddress && !options.forceSplit) {
    return street;
  }
  
  // Get field order for this address
  const fieldOrder = getFieldOrder(address);
  
  // Build address parts
  const parts = [];
  const usedFields = new Set();
  
  for (const field of fieldOrder) {
    const value = normalizeField(address, field);
    if (value && !usedFields.has(value.toLowerCase())) {
      // Skip country if not included
      if (field === 'country' && !includeCountry) continue;
      
      // Special handling for short format
      if (format === 'short' && !['city', 'country'].includes(field)) continue;
      
      parts.push(value);
      usedFields.add(value.toLowerCase());
    }
  }
  
  // Add any remaining fields that weren't in the order
  const allFields = ['street', 'city', 'state', 'province', 'zip', 'postalCode', 'country', 'district', 'apartment'];
  for (const field of allFields) {
    if (!usedFields.has(field)) {
      const value = normalizeField(address, field);
      if (value && !usedFields.has(value.toLowerCase())) {
        if (field === 'country' && !includeCountry) continue;
        if (format === 'short' && !['city', 'country'].includes(field)) continue;
        parts.push(value);
        usedFields.add(value.toLowerCase());
      }
    }
  }
  
  // Remove duplicates (case insensitive)
  const uniqueParts = parts.filter((part, index, self) => 
    self.findIndex(p => p.toLowerCase() === part.toLowerCase()) === index
  );
  
  // Format based on type
  if (format === 'multi-line') {
    return uniqueParts.join('\n');
  }
  
  if (format === 'short') {
    // Return just city and country
    const cityPart = uniqueParts.find(p => 
      p.toLowerCase() === (address.city || '').toLowerCase() ||
      p.toLowerCase() === (address.town || '').toLowerCase()
    );
    const countryPart = uniqueParts.find(p => 
      p.toLowerCase() === (address.country || '').toLowerCase()
    );
    
    if (cityPart && countryPart) {
      return `${cityPart}, ${countryPart}`;
    }
    return uniqueParts.slice(0, 2).join(separator);
  }
  
  return uniqueParts.join(separator);
};

/**
 * Get short address (city and country only)
 */
export const getShortAddress = (address) => {
  return formatAddress(address, { format: 'short' });
};

/**
 * Get multi-line address (for shipping labels, etc.)
 */
export const getMultiLineAddress = (address) => {
  return formatAddress(address, { format: 'multi-line' });
};

/**
 * Get address without country
 */
export const getAddressWithoutCountry = (address) => {
  return formatAddress(address, { includeCountry: false });
};

/**
 * Check if address is in a specific country
 */
export const isAddressInCountry = (address, countryName) => {
  if (!address) return false;
  
  const formatted = formatAddress(address);
  const country = address.country || '';
  
  return country.toLowerCase().includes(countryName.toLowerCase()) ||
         formatted.toLowerCase().includes(countryName.toLowerCase());
};

/**
 * Extract coordinates from address (if available)
 */
export const getAddressCoordinates = (address) => {
  if (!address) return null;
  
  if (address.coordinates) return address.coordinates;
  if (address.location) return address.location;
  
  if (address.lat && address.lng) {
    return { lat: address.lat, lng: address.lng };
  }
  
  if (address.latitude && address.longitude) {
    return { lat: address.latitude, lng: address.longitude };
  }
  
  return null;
};

/**
 * Format address for Google Maps URL
 */
export const getGoogleMapsUrl = (address) => {
  const formatted = formatAddress(address);
  if (!formatted) return '';
  
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formatted)}`;
};

/**
 * Format address for OpenStreetMap URL
 */
export const getOSMUrl = (address) => {
  const formatted = formatAddress(address);
  if (!formatted) return '';
  
  return `https://www.openstreetmap.org/search?query=${encodeURIComponent(formatted)}`;
};

export default formatAddress;